import React, { useState, useMemo } from "react";
import { LangContext } from "../../contexts/LangContext";
import en from "../../i18n/en";
import pl from "../../i18n/pl";
import '../../styles/balancePage.css';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const API_BASE = "https://flautreach.com/flautreach/api";

// Хелпер для подстановки переменных в строки
const formatStr = (str, vars = {}) => {
  let result = str;
  for (const [key, val] of Object.entries(vars)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), val);
  }
  return result;
};

export function BalancePage({ userInfo, onTopUp, onConvertTokens }) {
  const { lang } = React.useContext(LangContext);
  const t = lang === "pl" ? pl : en;
  const txt = t.balancePage;

  // ============ FEATURE FLAGS ============
  const CRYPTO_ENABLED = true;
  
  // ============ CONSTANTS ============
  const SCRAPING_RATE = 100;
  const CRYPTO_BONUS = 0.20;
  
  // ============ STATE ============
  const [topUpAmount, setTopUpAmount] = useState(20);
  const [showTopUpIntent, setShowTopUpIntent] = useState(false);
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);
  const [topUpError, setTopUpError] = useState("");
  const [topUpSuccess, setTopUpSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");

  const [convertFrom, setConvertFrom] = useState("balance");
  const [convertTo, setConvertTo] = useState("scraping");
  const [convertAmount, setConvertAmount] = useState(5);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const [convertError, setConvertError] = useState("");
  const [convertSuccess, setConvertSuccess] = useState(false);

  // ============ DATA FROM userInfo ============
  const pricePerGooglePage = parseFloat(userInfo.price_per_google_page);
  const pricePerLinkedinPost = parseFloat(userInfo.price_per_linkedin_post);
  const pricePerInstagramPost = parseFloat(userInfo.price_per_instagram_post);
  const pricePerInstagramPage = parseFloat(userInfo.price_per_instagram_page);
  const pricePerFacebookPage = parseFloat(userInfo.price_per_facebook_page);
  const priceForAiTokens = parseFloat(userInfo.price_for_ai_tokens);
  const minTopUp = parseFloat(userInfo.min_sum_for_topup);
  const balance = parseFloat(userInfo.balance || 0);

  // ============ CALCULATIONS ============
  const usdToScrapingTokens = (usd) => usd * SCRAPING_RATE;
  const aiTokensPerDollar = 1000000 / priceForAiTokens;
  const aiTokensCostPerOne = priceForAiTokens / 1000000;

  const googlePageTokens = usdToScrapingTokens(pricePerGooglePage);
  const linkedinPostTokens = usdToScrapingTokens(pricePerLinkedinPost);
  const instagramPostTokens = usdToScrapingTokens(pricePerInstagramPost);
  const instagramPageTokens = usdToScrapingTokens(pricePerInstagramPage);
  const facebookPageTokens = usdToScrapingTokens(pricePerFacebookPage);

  // ============ USAGE EXAMPLES ============
  const usageExamples = useMemo(() => {
    const st = userInfo.scraping_tokens || 0;
    const at = userInfo.ai_tokens || 0;
    
    return {
      scraping: {
        googlePages: Math.floor(st / googlePageTokens),
        linkedinPosts: Math.floor(st / linkedinPostTokens),
        instagramPosts: Math.floor(st / instagramPostTokens),
        facebookPages: Math.floor(st / facebookPageTokens),
      },
      ai: {
        small: Math.floor(at / 300),
        medium: Math.floor(at / 800),
        large: Math.floor(at / 1500),
      }
    };
  }, [userInfo.scraping_tokens, userInfo.ai_tokens]);

  // ============ CONVERT PREVIEW ============
  const convertPreview = useMemo(() => {
    if (convertFrom === convertTo) return null;
    
    let fromTokens = 0, toTokens = 0, cost = 0;

    if (convertFrom === "balance") {
      cost = convertAmount;
      toTokens = convertTo === "scraping" 
        ? Math.floor(usdToScrapingTokens(convertAmount))
        : Math.floor(aiTokensPerDollar * convertAmount);
    } else if (convertFrom === "scraping" && convertTo === "ai") {
      fromTokens = convertAmount;
      cost = convertAmount / SCRAPING_RATE;
      toTokens = Math.floor(aiTokensPerDollar * cost);
    } else if (convertFrom === "ai" && convertTo === "scraping") {
      fromTokens = convertAmount;
      cost = convertAmount * aiTokensCostPerOne;
      toTokens = Math.floor(usdToScrapingTokens(cost));
    }

    return { fromTokens, toTokens, cost };
  }, [convertFrom, convertTo, convertAmount]);

  // ============ API CALLS ============
  const createTopUpIntent = async (amount) => {
    const token = getCookie("authToken");
    const response = await fetch(`${API_BASE}/billing/intent/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ amount }),
    });
    if (!response.ok) throw new Error(txt.topUpFailed);
    return response.json();
  };

  const createInvoice = async (amount, paymentMethod) => {
    const token = getCookie("authToken");
    const response = await fetch(`${API_BASE}/billing/invoice/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ amount, payment_method: paymentMethod }),
    });
    if (!response.ok) throw new Error(txt.invoiceFailed);
    return response.json();
  };

  const convertTokensApi = async (fromType, toType, fromAmount) => {
    const token = getCookie("authToken");
    const response = await fetch(`${API_BASE}/billing/convert/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        from_type: fromType,
        to_type: toType,
        from_amount: fromAmount,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || txt.convertFailed);
    }
    return response.json();
  };

  // ============ HANDLERS ============
  const resetTopUp = () => {
    setShowTopUpIntent(false);
    setShowPaymentMethod(false);
    setTopUpError("");
    setPaymentLink("");
  };

  const handleTopUpIntent = () => {
    if (topUpAmount <= 0) {
      setTopUpError(txt.invalidAmount);
      return;
    }
    setShowTopUpIntent(true);
    setShowPaymentMethod(false);
    setTopUpError("");
    setPaymentLink("");
  };

  const handleAmountConfirm = async () => {
    setLoading(true);
    setTopUpError("");
    
    try {
      await createTopUpIntent(topUpAmount);
      
      if (topUpAmount < minTopUp) {
        setTopUpError(formatStr(txt.minimumTopUpError, {
          min: minTopUp.toFixed(2),
          amount: topUpAmount.toFixed(2),
          diff: (minTopUp - topUpAmount).toFixed(2),
        }));
        setShowPaymentMethod(false);
        return;
      }
      
      if (!CRYPTO_ENABLED) {
        setTopUpError(txt.cardNotWorkingError);
        setShowPaymentMethod(false);
        return;
      }
      
      setShowPaymentMethod(true);
    } catch (err) {
      setTopUpError(err.message || txt.topUpFailed);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (method) => {
    if (method === "card") {
      setTopUpError(txt.cardNotWorkingError);
      return;
    }

    setLoading(true);
    setTopUpError("");
    
    try {
      const result = await createInvoice(topUpAmount, method);
      setPaymentLink(result.payment_link);
      setTopUpSuccess(true);
      setShowPaymentMethod(false);
      
      window.open(result.payment_link, '_blank', 'noopener,noreferrer');
      
      if (onTopUp) {
        onTopUp({
          amount: topUpAmount,
          effectiveAmount: result.effective_amount,
          bonus: result.bonus,
          paymentMethod: method,
          paymentLink: result.payment_link,
        });
      }
    } catch (err) {
      setTopUpError(err.message || txt.invoiceFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleConvertTokens = async () => {
    if (!convertPreview || convertPreview.cost <= 0) return;
    
    setConvertLoading(true);
    setConvertError("");
    setConvertSuccess(false);
    
    try {
      const result = await convertTokensApi(convertFrom, convertTo, convertAmount);
      setConvertSuccess(true);
      setShowConvertConfirm(false);
      
      if (onConvertTokens) {
        onConvertTokens(result);
      }
      
      setTimeout(() => setConvertSuccess(false), 3000);
    } catch (err) {
      setConvertError(err.message || txt.convertFailed);
    } finally {
      setConvertLoading(false);
    }
  };

  const formatTokens = (val) => {
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toLocaleString();
  };

  // ============ FORMAT HELPERS ============
  const scrapingTokensDisplay = Number(userInfo.scraping_tokens).toFixed(3);
  const aiTokensDisplay = Number(userInfo.ai_tokens).toLocaleString();

  // ============ RENDER ============
  return (
    <div className="add-rule-wrap" style={{ maxWidth: 900 }}>
      
      {/* HEADER */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{txt.headerTitle}</h2>
        <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: 13 }}>{txt.headerSubtitle}</p>
      </div>

      {/* BALANCE CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
        <div className="section-card" style={{ marginBottom: 0 }}>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8, fontWeight: 600 }}>{txt.balanceLabel}</div>
          <div style={{ fontSize: "clamp(24px, 5vw, 32px)", fontWeight: 800, color: "var(--dark)" }}>{txt.dollarPrefix}{balance.toFixed(2)}</div>
        </div>
        <div className="section-card" style={{ marginBottom: 0 }}>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8, fontWeight: 600 }}>{txt.scrapingTokensLabel}</div>
          <div style={{ fontSize: "clamp(24px, 5vw, 32px)", fontWeight: 800, color: "var(--dark)" }}>{scrapingTokensDisplay}</div>
          <div style={{ fontSize: 15, color: "var(--muted)", marginTop: 14 }}>
            {formatStr(txt.usdToTokensRate, { rate: SCRAPING_RATE })}
          </div>
        </div>
        <div className="section-card" style={{ marginBottom: 0 }}>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8, fontWeight: 600 }}>{txt.aiTokensLabel}</div>
          <div style={{ fontSize: "clamp(24px, 5vw, 32px)", fontWeight: 800, color: "var(--dark)" }}>{aiTokensDisplay}</div>
          <div style={{ fontSize: 15, color: "var(--muted)", marginTop: 14 }}>
            {formatStr(txt.aiTokensRate, { price: priceForAiTokens })}
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 14 }}>{txt.aiTokensFormulaDesc}</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 14 }}>{txt.aiTokensFormula}</div>
        </div>
      </div>

      {/* WHAT YOU CAN DO */}
      <div className="section-card" style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{txt.whatYouCanDo}</h3>
        <p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 16px" }}>{txt.whatYouCanDoDesc}</p>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: "var(--dark)" }}>
            {txt.with200Scraping}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            {(() => {
              const st = 200;
              const safeDiv = (tokens, rate) => rate > 0 ? Math.floor(tokens / rate) : 0;
              return [
                { label: txt.googlePages, value: safeDiv(st, googlePageTokens), sub: formatStr(txt.tokensPerPage, { tokens: googlePageTokens.toFixed(3) }), color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
                { label: txt.linkedinPosts, value: safeDiv(st, linkedinPostTokens), sub: formatStr(txt.tokensPerPost, { tokens: linkedinPostTokens.toFixed(3) }), color: "#166534", bg: "#f0fdf4", border: "#bbf7d0" },
                { label: txt.instagramPosts, value: safeDiv(st, instagramPostTokens), sub: formatStr(txt.tokensPerPost, { tokens: instagramPostTokens.toFixed(3) }), color: "#92400e", bg: "#fef3c7", border: "#fcd34d" },
                { label: txt.facebookPages, value: safeDiv(st, facebookPageTokens), sub: formatStr(txt.tokensPerPage, { tokens: facebookPageTokens.toFixed(3) }), color: "#6b21a8", bg: "#faf5ff", border: "#d8b4fe" },
              ].map((item, i) => (
                <div key={i} style={{ padding: 16, background: item.bg, borderRadius: 10, border: `1px solid ${item.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 6, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase" }}>{txt.or}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: item.color, lineHeight: 1.2 }}>{item.value.toLocaleString()}</div>
                  <div style={{ fontSize: 12, color: item.color, fontWeight: 600, marginTop: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 10, color: item.color, marginTop: 2, opacity: 0.7 }}>{item.sub}</div>
                </div>
              ));
            })()}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: "var(--dark)" }}>
            {txt.with1MAI}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            {(() => {
              const at = 1000000;
              const safeDiv = (tokens, rate) => rate > 0 ? Math.floor(tokens / rate) : 0;
              return [
                { label: txt.smallContext, sub: formatStr(txt.tokensPerReq, { tokens: 300 }), value: safeDiv(at, 300) },
                { label: txt.mediumContext, sub: formatStr(txt.tokensPerReq, { tokens: 800 }), value: safeDiv(at, 800) },
                { label: txt.largeContext, sub: formatStr(txt.tokensPerReq, { tokens: 1500 }), value: safeDiv(at, 1500) },
              ].map((item, i) => (
                <div key={i} style={{ padding: 16, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 6, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase" }}>{txt.or}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "var(--dark)", lineHeight: 1.2 }}>{item.value.toLocaleString()}</div>
                  <div style={{ fontSize: 12, color: "var(--dark)", fontWeight: 600, marginTop: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{item.sub}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* TOP UP */}
      <div className="section-card" style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{txt.topUpTitle}</h3>
        <p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 16px" }}>
          {CRYPTO_ENABLED ? txt.topUpDescCrypto : txt.topUpDescCard}
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, display: "block" }}>{txt.amountLabel}</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {txt.presetAmounts.map(amount => (
              <button
                key={amount}
                onClick={() => { setTopUpAmount(amount); resetTopUp(); }}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: `1.5px solid ${topUpAmount === amount ? "var(--blue)" : "var(--border)"}`,
                  background: topUpAmount === amount ? "var(--blue-light)" : "#fff",
                  color: topUpAmount === amount ? "var(--blue)" : "var(--dark)",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {txt.dollarPrefix}{amount}
              </button>
            ))}
          </div>
          <input
            className="form-input"
            type="number"
            value={topUpAmount}
            onChange={(e) => { setTopUpAmount(Number(e.target.value)); resetTopUp(); }}
            min={1}
            step={5}
            style={{ width: "100%", paddingLeft: 14 }}
          />
        </div>

        {/* Success */}
        {topUpSuccess && (
          <div style={{ padding: 14, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#166534" }}>
            {txt.invoiceCreated}{" "}
            <a href={paymentLink} target="_blank" rel="noopener noreferrer" style={{ color: "#166534", fontWeight: 700, textDecoration: "underline" }}>
              {txt.clickToPay}
            </a>
          </div>
        )}

        {/* Step 1: Confirm amount */}
        {showTopUpIntent && !showPaymentMethod && !topUpSuccess && (
          <div style={{ padding: 16, background: "#f8fafc", border: "1px solid var(--border)", borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{txt.confirmAmount}</div>
            
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>{txt.youPay}</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{txt.dollarPrefix}{topUpAmount.toFixed(2)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>{txt.youGet}</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{txt.dollarPrefix}{topUpAmount.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>
                  {formatStr(txt.equalsTo, {
                    scraping: usdToScrapingTokens(topUpAmount).toLocaleString(),
                    ai: formatTokens(Math.floor(aiTokensPerDollar * topUpAmount)),
                  })}
                </div>
              </div>
            </div>

            {CRYPTO_ENABLED && (
              <div style={{ padding: 10, background: "#faf5ff", borderRadius: 6, marginBottom: 16, fontSize: 12, color: "#7c3aed" }}>
                {formatStr(txt.cryptoBonus, { amount: (topUpAmount * 1.2).toFixed(2) })}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handleAmountConfirm}
                disabled={loading}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: "none", background: "var(--dark)", color: "#fff", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: loading ? 0.7 : 1 }}
              >
                {loading ? txt.processing : txt.continueBtn}
              </button>
              <button
                onClick={resetTopUp}
                style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid var(--border)", background: "#fff", color: "var(--muted)", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                {txt.cancelBtn}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Payment method */}
        {showTopUpIntent && showPaymentMethod && !topUpSuccess && CRYPTO_ENABLED && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{txt.choosePaymentMethod}</div>
            
            <div
              onClick={() => handlePayment("card")}
              style={{ padding: 14, borderRadius: 8, border: "1px solid var(--border)", marginBottom: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, background: "#fff" }}
            >
              <span style={{ fontSize: 24 }}>💳</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{txt.cardLabel}</div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>{txt.cardNotAvailable}</div>
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "var(--muted)" }}>{txt.dollarPrefix}{topUpAmount.toFixed(2)}</div>
            </div>

            <div
              onClick={() => handlePayment("crypto")}
              style={{ padding: 14, borderRadius: 8, border: "1px solid #d8b4fe", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, background: "#faf5ff" }}
            >
              <span style={{ fontSize: 24 }}>₿</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#5b21b6" }}>{txt.cryptoLabel}</div>
                <div style={{ fontSize: 11, color: "#7c3aed" }}>{txt.cryptoDesc}</div>
                <span style={{ display: "inline-block", marginTop: 4, padding: "2px 8px", background: "#7c3aed", color: "#fff", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{txt.cryptoBonusBadge}</span>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#5b21b6" }}>{txt.dollarPrefix}{(topUpAmount).toFixed(2)}</div>
                <div style={{ fontSize: 10, color: "#9333ea" }}>+{txt.dollarPrefix}{(topUpAmount * 0.2).toFixed(2)} {txt.bonus}</div>
              </div>
            </div>

            <button
              onClick={() => { setShowPaymentMethod(false); setTopUpError(""); }}
              style={{ width: "100%", marginTop: 12, padding: 8, borderRadius: 8, border: "1px solid var(--border)", background: "#fff", color: "var(--muted)", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              {txt.backBtn}
            </button>
          </div>
        )}

        {/* Error */}
        {topUpError && (
          <div style={{ padding: 10, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, marginBottom: 16, fontSize: 12, color: "#991b1b" }}>
            {topUpError}
          </div>
        )}

        {!showTopUpIntent && !topUpSuccess && (
          <button
            className="btn-primary"
            onClick={handleTopUpIntent}
            disabled={topUpAmount <= 0}
            style={{ width: "100%", padding: 12, fontSize: 14, fontWeight: 700, opacity: topUpAmount <= 0 ? 0.5 : 1 }}
          >
            {formatStr(txt.topUpBtn, { amount: topUpAmount.toFixed(2) })}
          </button>
        )}
      </div>

      {/* CONVERT */}
      {/* CONVERT */}
<div className="section-card">
  <h3 className="section-title">{txt.convertTitle}</h3>
  <p className="section-desc">{txt.convertDesc}</p>

  <div className="convert-row">
    {/* FROM */}
    <div className="convert-side">
      <label className="field-label">{txt.fromLabel}</label>
      <select
        className="select"
        value={convertFrom}
        onChange={(e) => { setConvertFrom(e.target.value); setShowConvertConfirm(false); }}
      >
        <option value="balance">{formatStr(txt.balanceOption, { balance: balance.toFixed(2) })}</option>
        <option value="scraping">{formatStr(txt.scrapingOption, { tokens: formatTokens(userInfo.scraping_tokens || 0) })}</option>
        <option value="ai">{formatStr(txt.aiOption, { tokens: formatTokens(userInfo.ai_tokens || 0) })}</option>
      </select>
      <div className="input-wrapper">
        <input
          className="input"
          type="number"
          placeholder={convertFrom === "balance" ? txt.dollarPrefix : "tokens"}
          value={convertAmount}
          onChange={(e) => { setConvertAmount(Number(e.target.value)); setShowConvertConfirm(false); }}
          min={1}
          step={convertFrom === "balance" ? 5 : 100}
        />
      </div>
    </div>

    {/* ARROW */}
    <div className="convert-arrow">→</div>

    {/* TO */}
    <div className="convert-side">
      <label className="field-label">{txt.toLabel}</label>
      <select
        className="select"
        value={convertTo}
        onChange={(e) => { setConvertTo(e.target.value); setShowConvertConfirm(false); }}
      >
        <option value="scraping">{txt.scrapingTokensOption}</option>
        <option value="ai">{txt.aiTokensOption}</option>
      </select>
      <div className="preview-wrapper">
        <div className="preview-badge">
          {convertPreview && convertPreview.toTokens > 0 ? (
            <>
              <span className="preview-tokens">{formatTokens(convertPreview.toTokens)}</span>
              {convertPreview.cost > 0 && (
                <span className="preview-cost">
                  {formatStr(txt.approxUsd, { cost: convertPreview.cost.toFixed(4) })}
                </span>
              )}
            </>
          ) : (
            <span className="preview-empty">—</span>
          )}
        </div>
      </div>
    </div>
  </div>

  {convertSuccess && <div className="msg success">{txt.convertSuccess}</div>}
  {convertError && <div className="msg error">{convertError}</div>}

  {showConvertConfirm && convertPreview && (
    <div className="confirm-box">
      <div className="confirm-text">
        {convertFrom === "balance"
          ? formatStr(txt.convertConfirmBalance, {
              amount: convertAmount,
              tokens: formatTokens(convertPreview.toTokens),
              type: convertTo,
            })
          : formatStr(txt.convertConfirmTokens, {
              fromAmount: formatTokens(convertAmount),
              fromType: convertFrom,
              toAmount: formatTokens(convertPreview.toTokens),
              toType: convertTo,
            })}
      </div>
      <div className="confirm-buttons">
        <button
          className="btn confirm-btn"
          onClick={handleConvertTokens}
          disabled={convertLoading}
        >
          {convertLoading ? txt.converting : txt.confirmBtn}
        </button>
        <button
          className="btn cancel-btn"
          onClick={() => setShowConvertConfirm(false)}
        >
          {txt.cancelBtn}
        </button>
      </div>
    </div>
  )}

  <button
    className="btn btn-primary btn-full"
    onClick={() => setShowConvertConfirm(true)}
    disabled={!convertPreview || convertPreview.cost <= 0 || convertFrom === convertTo}
  >
    {txt.convertBtn}
  </button>
</div>


</div>
  );
}